<script setup>
/* global axios */
// Box "Carteira de Cliente" na lateral da conversa — deixa o agente marcar/desmarcar
// este contato (nesta caixa de entrada) como cliente dele, sem sair da conversa.
// Transferir o dono é ação de administrador, feita na tela da Carteira de Cliente.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const currentChat = useMapGetter('getSelectedChat');
const currentUserId = useMapGetter('getCurrentUserID');
const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const inboxId = computed(() => currentChat.value?.inbox_id);

const loading = ref(true);
const saving = ref(false);
const vinculo = ref(null);

const isMine = computed(() => vinculo.value?.user_id === currentUserId.value);

const load = async () => {
  if (!contactId.value || !inboxId.value) {
    vinculo.value = null;
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const { data } = await axios.get(`${base}/wallet_contacts/lookup`, {
      params: { inbox_id: inboxId.value, contact_id: contactId.value },
    });
    vinculo.value = data.data;
  } catch (e) {
    vinculo.value = null;
  } finally {
    loading.value = false;
  }
};

const addToMine = async () => {
  saving.value = true;
  try {
    const { data } = await axios.post(`${base}/wallet_contacts`, {
      contact_id: contactId.value,
      inbox_id: inboxId.value,
    });
    vinculo.value = data.data;
    useAlert(t('WALLET.api.addSuccess'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('WALLET.api.error'));
  } finally {
    saving.value = false;
  }
};

const removeFromMine = async () => {
  if (!vinculo.value) return;
  saving.value = true;
  try {
    await axios.delete(`${base}/wallet_contacts/${vinculo.value.id}`);
    vinculo.value = null;
    useAlert(t('WALLET.api.removeSuccess'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('WALLET.api.error'));
  } finally {
    saving.value = false;
  }
};

watch(() => props.conversationId, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-2.5 px-3.5 pt-2.5 pb-3.5">
    <div v-if="loading" class="text-xs text-n-slate-10">
      {{ t('WALLET.loading') }}
    </div>

    <template v-else-if="vinculo">
      <p v-if="isMine" class="text-xs text-n-slate-11">
        {{ t('WALLET.box.expiresIn', { count: vinculo.days_left }) }}
      </p>
      <p v-else class="text-xs text-n-slate-11">
        {{ t('WALLET.box.ownedBy', { agent: vinculo.user_name }) }}
      </p>
      <button
        v-if="isMine"
        class="w-full px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1 disabled:opacity-50"
        :disabled="saving"
        @click="removeFromMine"
      >
        {{ t('WALLET.box.removeFromMine') }}
      </button>
    </template>

    <template v-else>
      <button
        class="w-full px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-50"
        :disabled="saving"
        @click="addToMine"
      >
        {{ t('WALLET.box.addToMine') }}
      </button>
    </template>
  </div>
</template>
